import { useEffect, useState } from "react";
import {
  completeOracleWithdraw,
  getOraclePool,
  listOracleActs,
  listOraclePools,
  listOraclePositions,
  type OracleAct,
  type OraclePool,
  type OraclePosition,
} from "@/api/oracle";
import { errorMessage } from "@/api/auth";
import { useSession } from "@/session/SessionContext";
import { useToast } from "@/toast/ToastContext";

export function OraclePanel() {
  const { token } = useSession();
  const toast = useToast();
  const [pools, setPools] = useState<OraclePool[]>([]);
  const [detail, setDetail] = useState<OraclePool | null>(null);
  const [positions, setPositions] = useState<OraclePosition[]>([]);
  const [acts, setActs] = useState<OracleAct[]>([]);
  const [stars, setStars] = useState(5);
  const [caption, setCaption] = useState("");
  const [busyId, setBusyId] = useState<number | null>(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    listOraclePools()
      .then(setPools)
      .catch((e) => setErr(errorMessage(e)));
  }, []);

  async function open(code: string) {
    setErr("");
    try {
      const p = await getOraclePool(code);
      setDetail(p);
      const a = await listOracleActs(code).catch(() => []);
      setActs(a);
      if (token) {
        const items = await listOraclePositions(token, code).catch(() => []);
        setPositions(items);
      } else {
        setPositions([]);
      }
    } catch (e) {
      setErr(errorMessage(e));
    }
  }

  async function copy(addr: string) {
    try {
      await navigator.clipboard.writeText(addr);
      toast.ok("کپی");
    } catch {
      toast.err("کپی");
    }
  }

  async function onWithdraw(pos: OraclePosition) {
    if (!token || !detail?.code) return;
    setBusyId(pos.id);
    try {
      await completeOracleWithdraw(token, detail.code, {
        position_id: pos.id,
        stars,
        caption: caption.trim() || undefined,
        force_unlock: true,
      });
      toast.ok("withdraw");
      const items = await listOraclePositions(token, detail.code).catch(() => []);
      setPositions(items);
      const p = await getOraclePool(detail.code);
      setDetail(p);
      setActs(await listOracleActs(detail.code).catch(() => []));
    } catch (e) {
      toast.err(errorMessage(e));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="panel">
      {err && <p className="net-error">{err}</p>}
      {!detail && (
        <div className="quick-grid">
          {!pools.length && !err && <p className="muted">۰</p>}
          {pools.map((p) => {
            const apy =
              p.tiers?.[0]?.yield_apy_bps != null
                ? `${(Number(p.tiers[0].yield_apy_bps) / 100).toFixed(2)}%`
                : "—";
            const score =
              p.performance_score != null ? Number(p.performance_score).toFixed(2) : "—";
            return (
              <button
                key={p.code}
                type="button"
                className="action-card"
                onClick={() => void open(p.code)}
              >
                <b>{p.title || p.code}</b>
                <small className="muted">
                  {p.kind} · {p.token_symbol} · {apy}
                </small>
                <small className="muted">TVL {Number(p.tvl_usdt || 0).toFixed(2)}</small>
                <small className="ltr muted">★ {score}</small>
              </button>
            );
          })}
        </div>
      )}
      {detail && (
        <div className="card">
          <button type="button" className="ghost" onClick={() => setDetail(null)}>
            بازگشت
          </button>
          <b>{detail.title || detail.code}</b>
          {detail.description && <p className="muted">{detail.description}</p>}
          <p className="muted">min {Number(detail.min_deposit_usdt || 0)} USDT</p>
          <p className="muted">mint {detail.live_mint_enabled ? "on" : "off"}</p>
          <p className="ltr muted">
            ★ {detail.performance_score != null ? Number(detail.performance_score).toFixed(2) : "—"}{" "}
            · {detail.rating_count ?? 0}
          </p>
          <div className="code-row">
            <code className="addr ltr">{detail.bsc_usdt_address || "—"}</code>
            {detail.bsc_usdt_address && (
              <button
                type="button"
                className="chip"
                onClick={() => void copy(detail.bsc_usdt_address || "")}
              >
                کپی
              </button>
            )}
          </div>
          <ul className="tier-list">
            {(detail.tiers || []).map((t, i) => (
              <li key={i}>
                {t.label || "—"}: {t.lock_days}d / {t.unlock_days}d /{" "}
                {(Number(t.yield_apy_bps || 0) / 100).toFixed(2)}%
              </li>
            ))}
          </ul>

          <div className="open-orders">
            <div className="row-between">
              <b>positions</b>
              {!token && <span className="muted">SESSION</span>}
            </div>
            <div className="mode-tabs">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  className={stars === n ? "chip active" : "chip"}
                  onClick={() => setStars(n)}
                >
                  ★{n}
                </button>
              ))}
            </div>
            <label>
              caption
              <input
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                maxLength={200}
              />
            </label>
            {!positions.length && <p className="muted">۰</p>}
            {positions.map((pos) => (
              <div key={pos.id} className="row-between open-row">
                <span className="ltr">
                  #{pos.id} {pos.status} claim {pos.claimable_usdt ?? "—"}
                </span>
                <button
                  type="button"
                  className="chip"
                  disabled={!token || busyId === pos.id || pos.status === "closed"}
                  onClick={() => void onWithdraw(pos)}
                >
                  {busyId === pos.id ? "…" : "redeem"}
                </button>
              </div>
            ))}
          </div>

          <div className="open-orders">
            <b>acts</b>
            {!acts.length && <p className="muted">۰</p>}
            {acts.slice(0, 8).map((a) => (
              <div key={a.id} className="row-between open-row">
                <span className="ltr">
                  ★{a.stars} {a.caption || "—"} · {a.twin_status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
